/**
 * Funções puras da prévia da mensagem citada por uma resposta (âncora da resposta, 26/10/2026).
 *
 * O servidor monta `respondendo_a` (`CitadaDaResposta`) e o texto de cada tipo nasce aqui, em UM lugar, para a
 * prévia da bolha (`PreviaDaResposta`), a barra "Respondendo a" da caixa de escrever e o nome acessível dizerem as
 * mesmas palavras (desenho `docs/superpowers/specs/2026-10-26-chat-ancora-da-resposta-tela.md`, §2 e §10).
 * Sem React: o ícone sai como um nome (`IconeDaOriginal`) que quem desenha mapeia para o lucide.
 *
 * Nunca devolve vazio nem `undefined`/`null`/`NaN`/`[object Object]`: o `tipo` vem do servidor e cresce, e todo
 * ramo tem um rótulo.
 */

import { formatDuration } from "@/lib/chat/formatarDuracao";
import { textoDaPrevia } from "@/lib/chamadas/textosDaLigacao";
import type { ChatMessage, CitadaDaResposta } from "@/types/chat";

/**
 * O `data-testid` da prévia dentro da bolha da resposta `messageId` (a mensagem que CITA, não a citada; desenho §9). A
 * `PreviaDaResposta` o escreve e o `usePilhaDoVoltar` o procura no DOM: o padrão mora aqui para os dois não divergirem.
 */
export function testIdDaPrevia(messageId: string): string {
  return `previa-da-resposta-${messageId}`;
}

/** O trecho (texto ou legenda) cabe em 140 caracteres: a mesma conta de `TAMANHO_DO_TRECHO` do servidor. */
export const TAMANHO_DO_TRECHO = 140;

/**
 * O trecho como o servidor o faz (`_trecho` de `chat_respondendo_a.py`): brancos juntados num espaço só, corte em
 * `TAMANHO_DO_TRECHO` PONTOS DE CÓDIGO (o `len()` do Python: um emoji é 1 e nunca é cortado ao meio); vazio → `null`.
 * Repetida no front para o cache manter a prévia viva quando a original é editada.
 */
export function trechoDaCitada(texto: string | null | undefined): string | null {
  if (typeof texto !== "string") return null;
  const junto = texto.split(/\s+/).filter(Boolean).join(" ");
  if (!junto) return null;
  const pontos = [...junto];
  return (pontos.length > TAMANHO_DO_TRECHO ? pontos.slice(0, TAMANHO_DO_TRECHO) : pontos).join("");
}

/** O ícone de cada tipo (nomes, não componentes): `Image`, `Paperclip`, `Mic`, `Sticker`, `Film`, `Phone`, `Video`, `Info`, `MessageSquare`. */
export type IconeDaOriginal =
  | "foto"
  | "arquivo"
  | "audio"
  | "figurinha"
  | "gif"
  | "ligacao"
  | "video"
  | "sistema"
  | "mensagem";

/** O texto e o ícone da original na prévia. `icone` é `null` no texto puro e na apagada. */
export interface DescricaoDaOriginal {
  icone: IconeDaOriginal | null;
  texto: string;
}

/** Texto não vazio (aparado) ou `null`: o `trecho`/`filename` só de espaços vale como ausente. */
function textoOuNulo(valor: string | null | undefined): string | null {
  if (typeof valor !== "string") return null;
  const aparado = valor.trim();
  return aparado || null;
}

/** "Áudio · 00:12"; sem duração válida (nula, `NaN`, infinita, zero ou negativa), só "Áudio". */
function rotuloDoAudio(duracaoSec: number | null): string {
  return typeof duracaoSec === "number" && Number.isFinite(duracaoSec) && duracaoSec > 0
    ? `Áudio · ${formatDuration(duracaoSec)}`
    : "Áudio";
}

/**
 * O que a prévia diz da original, por tipo (desenho §2): a apagada vale mais que qualquer tipo (a lápide do servidor
 * vem como `system_event`); depois, o trecho ou o rótulo do tipo. Tipo desconhecido cai em "Mensagem".
 */
export function descricaoDaOriginal(c: CitadaDaResposta): DescricaoDaOriginal {
  if (c.apagada) return { icone: null, texto: "Mensagem apagada" };

  const trecho = textoOuNulo(c.trecho);
  switch (c.tipo) {
    case "text":
    case "mention":
    case "reply":
      return { icone: null, texto: trecho ?? "Mensagem" };
    case "attachment":
      // O servidor só manda `arquivo` para o que NÃO é foto do chat (PDF, planilha, foto do Drive): foto = `arquivo` nulo.
      return c.arquivo
        ? { icone: "arquivo", texto: textoOuNulo(c.arquivo.filename) ?? "Arquivo" }
        : { icone: "foto", texto: trecho ?? "Foto" };
    case "audio":
      return { icone: "audio", texto: trecho ?? rotuloDoAudio(c.duracao_sec) };
    case "sticker":
      return { icone: "figurinha", texto: "Figurinha" };
    case "gif":
      return { icone: "gif", texto: "GIF" };
    case "call_event":
      return {
        icone: c.ligacao?.media === "video" ? "video" : "ligacao",
        texto: textoDaPrevia({
          event: c.ligacao?.event ?? undefined,
          media: c.ligacao?.media,
          duration_seconds: c.ligacao?.duration_seconds,
        }),
      };
    case "system_event":
      return { icone: "sistema", texto: trecho ?? "Mensagem do sistema" };
    default:
      return { icone: "mensagem", texto: trecho ?? "Mensagem" };
  }
}

/** A original é do próprio leitor? Sem leitor conhecido, ninguém é. A regra do "Você" e do "Ir para a sua mensagem". */
export function ehDoLeitor(
  c: Pick<CitadaDaResposta, "author_id">,
  leitorId: string | null | undefined
): boolean {
  return !!leitorId && c.author_id === leitorId;
}

/** Quem escreveu a original: "Você" para a própria pessoa, o nome do autor, ou "Usuário" quando o nome não veio. */
export function nomeDaOriginal(
  c: Pick<CitadaDaResposta, "author_id" | "author_name">,
  leitorId: string | null | undefined
): string {
  if (ehDoLeitor(c, leitorId)) return "Você";
  return textoOuNulo(c.author_name) ?? "Usuário";
}

type Conteudo = Record<string, unknown>;

/** `url` só se começa com `http` ou `/` (a regra do servidor): `data:` e o resto não viram miniatura. */
function soHttpOuCaminho(url: unknown): string | null {
  return typeof url === "string" && (url.startsWith("http") || url.startsWith("/")) ? url : null;
}

function textoDe(valor: unknown): string | null {
  return typeof valor === "string" ? valor : null;
}

/** Número finito ou `null`: o que não é número (nem `NaN`) não vira duração. */
function numeroOuNulo(valor: unknown): number | null {
  return typeof valor === "number" && Number.isFinite(valor) ? valor : null;
}

/**
 * A mensagem já carregada vista como a prévia que o servidor mandaria: o adaptador da barra "Respondendo a" (a
 * mensagem clicada ainda não tem `respondendo_a`, ela é a citada). Mesma tabela do servidor (`_previa_da`): a apagada
 * leva só o cabeçalho; foto do chat (`image/*` sem `document_id`) leva a miniatura e nenhum `arquivo`; o resto dos
 * anexos leva `arquivo`. O tipo vem de `content.__type ?? content.type` (o cache tem as duas formas).
 */
export function citadaDeMensagem(m: ChatMessage): CitadaDaResposta {
  const conteudo: Conteudo =
    m.content && typeof m.content === "object" ? (m.content as Conteudo) : {};
  const bruto = conteudo.__type ?? conteudo.type;
  const tipo = typeof bruto === "string" && bruto ? bruto : "desconhecido";

  const citada: CitadaDaResposta = {
    message_id: m.id,
    author_id: m.author_id,
    author_name: textoOuNulo(m.author_name),
    created_at: m.created_at,
    apagada: !!m.deleted_at,
    tipo,
    trecho: null,
    arquivo: null,
    duracao_sec: null,
    miniatura_url: null,
    ligacao: null,
  };
  if (citada.apagada) return citada; // nada do conteúdo de uma apagada sai

  switch (tipo) {
    case "text":
    case "mention":
    case "reply":
      citada.trecho = trechoDaCitada(textoDe(conteudo.text));
      break;
    case "attachment": {
      citada.trecho = trechoDaCitada(textoDe(conteudo.caption));
      const mime = textoDe(conteudo.mime);
      const fotoDoChat = !conteudo.document_id && !!mime?.startsWith("image/");
      if (fotoDoChat) {
        citada.miniatura_url = soHttpOuCaminho(conteudo.url);
      } else {
        citada.arquivo = { filename: textoDe(conteudo.filename), mime };
      }
      break;
    }
    case "audio":
      citada.trecho = trechoDaCitada(textoDe(conteudo.caption));
      citada.duracao_sec = numeroOuNulo(conteudo.duration_sec);
      break;
    case "sticker":
    case "gif":
      citada.miniatura_url = soHttpOuCaminho(conteudo.url);
      break;
    case "call_event": {
      const media = conteudo.media;
      citada.ligacao = {
        event: textoDe(conteudo.event),
        media: media === "audio" || media === "video" ? media : null,
        duration_seconds: numeroOuNulo(conteudo.duration_seconds),
      };
      break;
    }
  }
  return citada;
}
